<template>
  <el-dialog
    title="选择套餐并配置出行信息"
    :visible.sync="dialogVisible"
    width="1180px"
    top="5vh"
    class="purchase-dialog"
  >
    <div v-if="!packages.length" class="dialog-empty">暂无可用套餐</div>

    <div v-else class="purchase-layout">
      <div class="purchase-packages">
        <div class="purchase-block-title">套餐列表</div>
        <div class="purchase-package-list">
          <div
            v-for="pkg in packages"
            :key="pkg.pkg_no"
            class="purchase-package-card"
            :class="{ active: isActivePackage(pkg), disabled: isPackageUnavailable(pkg) }"
            @click="handlePackageClick(pkg)"
          >
            <div class="purchase-package-head">
              <div class="package-head-main">
                <div class="package-name">{{ pkg.pkg_name }}</div>
                <div
                  class="package-status"
                  :class="{
                    'package-status--loading': isPackageLoading(pkg),
                    'package-status--disabled': isPackageUnavailable(pkg),
                  }"
                >
                  {{ getPackageStatusText(pkg) }}
                </div>
              </div>
              <div class="package-price-row">
                <div class="package-price-label">参考价</div>
                <div class="package-price">¥ {{ pkg.b2c_min_price || 0 }}</div>
              </div>
            </div>
            <div class="purchase-package-foot">
              <span class="package-refund">{{ getPackageRefundDesc(pkg) || '以套餐规则为准' }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="purchase-config">
        <div class="purchase-config-scroll">
          <div v-if="currentPackageLoading" class="purchase-loading">
            <i class="el-icon-loading"></i>
            <span>套餐信息加载中...</span>
          </div>

          <div v-else-if="!currentPackageDetail" class="purchase-empty">
            <div v-if="currentPackageUnavailableMessage" class="purchase-unavailable-card">
              <div class="purchase-unavailable-title">当前套餐不可购买</div>
              <div class="purchase-unavailable-desc">当前套餐暂不可购买，请选择其他套餐</div>
            </div>
            <span v-else>请选择左侧套餐</span>
          </div>

          <template v-else>
            <shop-purchase-selection-summary
              :title="currentPackageDetail.pkg_name || currentPackage.pkg_name"
              :unit="currentItem && currentItem.unit ? currentItem.unit : ''"
              :display-date-text="displayDateText"
              :time-slot-text="selectedOptions.selectedTimeSlot ? getTimeSlotName(selectedOptions.selectedTimeSlot) : ''"
              :total-price-b2-c="totalPriceB2C"
              :min-price="currentPackageDetail.b2c_min_price || 0"
              :show-selected-list="false"
              :total-days="selectedOptions.totalDays"
              :selected-skus="selectedOptions.selectedSKUs"
              :get-sku-final-price-b2-c="getSkuFinalPriceB2C"
            />

            <div class="purchase-policy-card">
              <div class="purchase-policy-head">
                <div class="purchase-policy-title">退款规则</div>
                <div v-if="currentRefundDesc" class="purchase-policy-tag">{{ currentRefundDesc }}</div>
              </div>
              <div v-if="currentRefundLines.length" class="purchase-policy-list">
                <div
                  v-for="(line, index) in currentRefundLines"
                  :key="'refund-' + index"
                  class="purchase-policy-item"
                >
                  {{ line }}
                </div>
              </div>
              <div v-else class="purchase-policy-empty">以套餐规则为准</div>
            </div>

            <shop-purchase-spec-selector
              :show-date-picker="showDatePicker"
              :date-picker-value="datePickerValue"
              :date-picker-type="datePickerType"
              :date-picker-options="datePickerOptions"
              :sale-start-date="saleStartDate"
              :sale-end-date="saleEndDate"
              :display-date-text="displayDateText"
              :has-time-slots="hasTimeSlots"
              :time-slots="timeSlots"
              :selected-time-slot="selectedOptions.selectedTimeSlot"
              :available-specs="availableSpecs"
              :last-spec-multi="lastSpecMulti"
              :selected-specs="selectedSpecs"
              :matched-skus="matchedSKUs"
              :get-time-slot-name="getTimeSlotName"
              :get-spec-item-price="getSpecItemPrice"
              :get-spec-item-quantity="getSpecItemQuantity"
              :is-spec-item-available-for-date="isSpecItemAvailableForDate"
              :get-sku-final-price-b2-c="getSkuFinalPriceB2C"
              :is-sku-available-for-date="isSkuAvailableForDate"
              @date-input="$emit('date-input', $event)"
              @select-time-slot="$emit('select-time-slot', $event)"
              @select-spec="forwardSelectSpec"
              @increase-spec-quantity="forwardIncreaseSpecQuantity"
              @decrease-spec-quantity="forwardDecreaseSpecQuantity"
              @increase-sku-quantity="$emit('increase-sku-quantity', $event)"
              @decrease-sku-quantity="$emit('decrease-sku-quantity', $event)"
            />

            <shop-purchase-selection-summary
              :unit="currentItem && currentItem.unit ? currentItem.unit : ''"
              :show-overview="false"
              :total-days="selectedOptions.totalDays"
              :selected-skus="selectedOptions.selectedSKUs"
              :get-sku-final-price-b2-c="getSkuFinalPriceB2C"
            />
          </template>
        </div>

        <div class="purchase-config-footer">
          <div class="purchase-total">
            <span>共 {{ totalQuantity }} 份</span>
            <strong>¥ {{ totalPriceB2C }}</strong>
          </div>
          <div class="purchase-footer-actions">
            <el-button @click="dialogVisible = false">关闭</el-button>
            <el-button type="primary" @click="$emit('confirm')">立即购买</el-button>
          </div>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import ShopPurchaseSelectionSummary from './ShopPurchaseSelectionSummary.vue';
import ShopPurchaseSpecSelector from './ShopPurchaseSpecSelector.vue';

export default {
  name: 'ShopPurchaseDialog',
  components: {
    ShopPurchaseSelectionSummary,
    ShopPurchaseSpecSelector,
  },
  props: {
    visible: Boolean,
    packages: {
      type: Array,
      default() {
        return [];
      },
    },
    packageAvailability: {
      type: Object,
      default() {
        return {};
      },
    },
    currentPkgNo: {
      type: [String, Number],
      default: '',
    },
    currentPackage: {
      type: Object,
      default() {
        return {};
      },
    },
    currentPackageLoading: Boolean,
    currentPackageDetail: {
      type: Object,
      default() {
        return null;
      },
    },
    currentPackageUnavailableMessage: {
      type: String,
      default: '',
    },
    currentItem: {
      type: Object,
      default() {
        return null;
      },
    },
    selectedOptions: {
      type: Object,
      required: true,
    },
    showDatePicker: Boolean,
    datePickerValue: {
      type: [Array, String],
      default: '',
    },
    datePickerType: {
      type: String,
      default: 'date',
    },
    datePickerOptions: {
      type: Object,
      default() {
        return {};
      },
    },
    saleStartDate: {
      type: String,
      default: '',
    },
    saleEndDate: {
      type: String,
      default: '',
    },
    displayDateText: {
      type: String,
      default: '',
    },
    hasTimeSlots: Boolean,
    timeSlots: {
      type: Array,
      default() {
        return [];
      },
    },
    availableSpecs: {
      type: Array,
      default() {
        return [];
      },
    },
    lastSpecMulti: Boolean,
    selectedSpecs: {
      type: Object,
      default() {
        return {};
      },
    },
    matchedSKUs: {
      type: Array,
      default() {
        return [];
      },
    },
    totalQuantity: {
      type: Number,
      default: 0,
    },
    totalPriceB2C: {
      type: Number,
      default: 0,
    },
    getPackageRefundDesc: {
      type: Function,
      required: true,
    },
    getPackageRefundLines: {
      type: Function,
      required: true,
    },
    getTimeSlotName: {
      type: Function,
      required: true,
    },
    getSpecItemPrice: {
      type: Function,
      required: true,
    },
    getSpecItemQuantity: {
      type: Function,
      required: true,
    },
    isSpecItemAvailableForDate: {
      type: Function,
      required: true,
    },
    getSkuFinalPriceB2C: {
      type: Function,
      required: true,
    },
    isSkuAvailableForDate: {
      type: Function,
      required: true,
    },
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit('update:visible', value);
      },
    },
    currentRefundDesc() {
      const lines = this.currentRefundLines;
      return lines.length === 1 ? '' : this.getPackageRefundDesc(this.currentPackageDetail || this.currentPackage);
    },
    currentRefundLines() {
      const detail = this.currentPackageDetail || this.currentPackage;
      if (!detail || !this.getPackageRefundLines) return [];
      return this.getPackageRefundLines(detail).filter((line, index, list) => {
        return line && list.indexOf(line) === index;
      });
    },
  },
  methods: {
    isActivePackage(pkg) {
      return String(this.currentPkgNo) === String(pkg.pkg_no);
    },
    isPackageUnavailable(pkg) {
      const status = this.packageAvailability[String(pkg.pkg_no)];
      return !!(status && !status.loading && status.available === false);
    },
    isPackageLoading(pkg) {
      const status = this.packageAvailability[String(pkg.pkg_no)];
      return !!(status && status.loading);
    },
    getPackageStatusText(pkg) {
      if (this.isPackageLoading(pkg)) return '校验中';
      if (this.isPackageUnavailable(pkg)) return '不可购买';
      return '可购买';
    },
    handlePackageClick(pkg) {
      if (this.isPackageUnavailable(pkg) || this.isPackageLoading(pkg)) return;
      this.$emit('select-package', pkg);
    },
    forwardSelectSpec(specOid, specItemOid) {
      this.$emit('select-spec', specOid, specItemOid);
    },
    forwardIncreaseSpecQuantity(specOid, specItemOid) {
      this.$emit('increase-spec-quantity', specOid, specItemOid);
    },
    forwardDecreaseSpecQuantity(specOid, specItemOid) {
      this.$emit('decrease-spec-quantity', specOid, specItemOid);
    },
  },
};
</script>

<style scoped>
.dialog-empty,
.purchase-empty,
.purchase-loading {
  padding: 40px 0;
  text-align: center;
  color: #999;
}

.purchase-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 320px;
}

.purchase-layout {
  display: flex;
  gap: 20px;
  min-height: 620px;
  max-height: 72vh;
}

.purchase-packages {
  width: 360px;
  flex-shrink: 0;
}

.purchase-block-title {
  color: #20314a;
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 16px;
}

.purchase-package-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 620px;
  overflow: auto;
  padding-right: 6px;
}

.purchase-package-card {
  border: 1px solid #e5edf7;
  border-radius: 12px;
  padding: 16px;
  background: #fff;
  cursor: pointer;
  transition: 0.2s ease;
}

.purchase-package-card:hover,
.purchase-package-card.active {
  border-color: #1c5098;
  box-shadow: 0 10px 20px rgb(28 80 152 / 10%);
}

.purchase-package-head,
.purchase-package-foot,
.purchase-footer {
  display: flex;
  align-items: center;
}

.purchase-package-head {
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
}

.purchase-package-card.disabled {
  border-color: #e5e7eb;
  background: #f8fafc;
  cursor: not-allowed;
}

.purchase-package-card.disabled:hover {
  border-color: #d7dde7;
  box-shadow: none;
}

.package-head-main {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.purchase-package-foot {
  justify-content: space-between;
  margin-top: 12px;
  gap: 12px;
}

.package-name {
  color: #1f2d3d;
  font-size: 15px;
  font-weight: bold;
  line-height: 1.6;
}

.package-price-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
}

.package-price-label {
  flex: 1;
  min-width: 0;
  color: #8b97aa;
  font-size: 12px;
  line-height: 1.4;
}

.package-price {
  flex-shrink: 0;
  color: #1c5098;
  font-size: 20px;
  font-weight: bold;
  line-height: 1;
  white-space: nowrap;
  text-align: right;
}

.package-status {
  flex-shrink: 0;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(34, 197, 94, 0.12);
  color: #15803d;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
}

.package-status--loading {
  background: rgba(59, 130, 246, 0.12);
  color: #1d4ed8;
}

.package-status--disabled {
  background: rgba(239, 68, 68, 0.1);
  color: #b91c1c;
}

.package-refund {
  color: #9099a8;
  font-size: 12px;
  line-height: 1.6;
}

.purchase-config {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.purchase-config-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 8px;
}

.purchase-config-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 20px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e8eef6;
  background: #fff;
}

.purchase-policy-card {
  margin: 16px 0;
  padding: 16px 18px;
  border: 1px solid #e8eef6;
  border-radius: 12px;
  background: linear-gradient(180deg, #fbfdff 0%, #f5f9ff 100%);
}

.purchase-policy-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.purchase-policy-title {
  color: #20314a;
  font-size: 15px;
  font-weight: bold;
}

.purchase-policy-tag {
  color: #1c5098;
  font-size: 12px;
  font-weight: 600;
  background: rgb(28 80 152 / 8%);
  border-radius: 999px;
  padding: 4px 10px;
}

.purchase-policy-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.purchase-policy-item,
.purchase-policy-empty {
  color: #5b6575;
  font-size: 13px;
  line-height: 1.8;
}

.purchase-unavailable-card {
  width: 100%;
  max-width: 480px;
  padding: 22px 24px;
  border: 1px solid #f3d3d0;
  border-radius: 14px;
  background: linear-gradient(180deg, #fff8f7 0%, #fff1ef 100%);
  text-align: left;
}

.purchase-unavailable-title {
  color: #c45656;
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 8px;
}

.purchase-unavailable-desc {
  color: #8a5a5a;
  font-size: 14px;
  line-height: 1.8;
}

.purchase-total {
  display: flex;
  align-items: center;
  gap: 14px;
  color: #5b6575;
  white-space: nowrap;
}

.purchase-total strong {
  color: #1c5098;
  font-size: 28px;
}

.purchase-footer-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

@media (max-width: 1260px) {
  .purchase-layout {
    display: block;
    max-height: none;
  }

  .purchase-packages {
    width: 100%;
  }

  .purchase-config {
    margin-top: 20px;
  }

  .purchase-config-scroll {
    max-height: none;
    overflow: visible;
    padding-right: 0;
  }

  .purchase-config-footer {
    flex-direction: column;
    align-items: flex-end;
    gap: 16px;
  }

  .purchase-policy-head {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/shop/components/ShopPurchaseDialog.vue